import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  UserOutlined,
  LeftOutlined,
  WifiOutlined,
  SettingOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

import { CloudRegular, CloudOffRegular } from "@fluentui/react-icons";

import { Progress, Spin, Tooltip } from "antd";
import DownloadSaveNoticeModal from "@/components/ui/DownloadSaveNoticeModal";
import { debounce, DebouncedFunc } from "lodash";
import axios from "axios";

// import { Button } from "@/components/ui/button"

import AuthPage from "@/pages/AuthPage";
import MainPage from "@/pages/MainPage";
import ReportAdoptionPage from "@/pages/ReportAdoptionPage";
import SelectPage from "@/pages/SelectPage";
import SettingsPage from "@/pages/SettingsPage";

import useWordDocument from "@/hooks/useWordDocument";
import useBackendAPIs from "@/utility/useBackendAPIs";
import { useOrganizations } from "@/utility/useOrganizations";

import Footer from "@/components/Footer";
import Header from "@/components/Header";
import ErrorOverlay from "@/components/ErrorOverlay";
import DocumentReadinessModal from "@/components/DocumentReadinessModal/DocumentReadinessModal";
import { hydrateReportDownloadSections } from "@/utility/hydrateReportDownload";
import { buildDownloadedSectionList } from "@/utility/downloadedSectionList";
import { isMainDataReady } from "@/utility/mainDataReadiness";
import { resolveHomePage } from "@/utility/homePage";
import {
  AdoptionProgress,
  AdoptionSectionProgress,
  adoptionStageLabel,
  buildAdoptionSectionRows,
} from "@/utility/adoptReportDownload";

import { loadReportDownloadSource } from "@/utility/loadReportDownloadSource";
import { reparseReportDownload, probeReportRender } from "@/utility/reparseReportDownload";

import {
  ChatMessage,
  SourceDocument,
  FeedbackContent,
  RegenerateContent,
  QualityCheckContent,
  ErrorUI,
  EvalStatus,
  TemplateTag,
  UploadedFile,
} from "@/lib/types";
import {
  defaultError,
  defaultChatMessage,
  defaultFeedbackContent,
  defaultRegenerateContent,
  defaultQualityCheckContent,
} from "@/lib/defaults";
import { useEnvironment } from "@/context/EnvironmentContext";
import { useAuth } from "@/context/AuthContext";
import { wordTask } from "@/hooks/useWordDocumentUtils/wordTask";
import { ensureTaskpaneAutoOpens } from "@/utility/autoShowTaskpane";
import { DocumentReadinessNoticeData, getDocumentsNotReadyNoticeFromDocuments } from "@/utility/documentReadiness";
import { shouldGenerateWordComment } from "@/utility/shouldGenerateWordComment";
import Logger from "@/utility/logger";
import { hasSectionActiveTask } from "@/utility/sectionTaskState";
import { calculateSectionProgress } from "@/utility/sectionProgress";
import { markSignedOut } from "@/auth/officeAccount";

// Injected at build time from package.json "version" (webpack.DefinePlugin).
// Bump with `npm run version:bump` — do not edit this by hand.
const VERSION = __APP_VERSION__;

export default function Home() {
  const [location, setLocation] = useState<string>(
    "desktop"
    // `${Office.context.diagnostics.platform ? (Office.context.diagnostics.platform === Office.PlatformType.OfficeOnline ? "online" : "desktop") : "desktop"}`
  );
  const [signedIn, setSignedIn] = useState(false);
  const [signInNotice, setSignInNotice] = useState<string | null>(null);
  const [showOrganizationPicker, setShowOrganizationPicker] = useState(false);
  const [displayMainFunc, setDisplayMainFunc] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const [generatedSections, setGeneratedSections] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [settingsGroup, setSettingsGroup] = useState([]);
  const [templateTagsMap, setTemplateTagsMap] = useState<Record<string, TemplateTag>>({});
  const [flushing, setFlushing] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedReportTemplateId, setSelectedReportTemplateId] = useState<string | null>(null);
  const [sectionStatus, setSectionStatus] = useState<{ [key: string]: boolean | null }>({});
  const [selectsDisabled, setSelectsDisabled] = useState(false);
  const [sectionEvalStatus, setSectionEvalStatus] = useState<EvalStatus>({});
  const [selectAllSections, setSelectAllSections] = useState(false);
  const [outputDecision, setOutputDecision] = useState<{ [key: string]: boolean | null }>({});
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [sourceDialogContent, setSourceDialogContent] = useState<SourceDocument[]>([]);
  const [sectionDialogContent, setSectionDialogContent] = useState({ section: {} });
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackDialogContent, setFeedbackDialogContent] = useState<FeedbackContent>(defaultFeedbackContent);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerateDialogContent, setRegenerateDialogContent] = useState<RegenerateContent>(defaultRegenerateContent);
  const [qualityCheckDialogOpen, setQualityCheckDialogOpen] = useState(false);
  const [qualityCheckDialogContent, setQualityCheckDialogContent] = useState<QualityCheckContent[]>([
    // { name: "Model Comparison", value: 0.575, description: "The OpenAI model provided better results with the content being more similar to the prompt template." }, { name: "Confidence", value: 0.8, description: "" }, { name: "Similarity", value: 0.3, description: "" }
  ]);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [bookmarks, setBookmarks] = useState<string[]>([]);
  const [searchType, setSearchType] = useState<string>("hybrid"); // "hybrid" | "similarity"

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Use environment context
  const { selectedEnvironment, setSelectedEnvironmentById, environments } = useEnvironment();
  const baseUrl = selectedEnvironment?.url;
  const { tokens, organizationId, logout: jwtLogout, entraLogout, authProvider, axios: authAxios } = useAuth();
  const { switchOrganization } = useOrganizations();
  const [projectDetailsExpanded, setProjectDetailsExpanded] = useState(false);
  const [projectPlaceholderDetails, setProjectPlaceholderDetails] = useState([]);
  const [showError, setShowError] = useState<ErrorUI>(defaultError);
  const [messages, setMessages] = useState<ChatMessage[]>(defaultChatMessage);
  const [test, setTest] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadedAgentFiles, setUploadedAgentFiles] = useState<UploadedFile[]>([]);

  const [messageChatId, setMessageChatId] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState(false);
  const savingDisabledRef = useRef(true);
  const isDownloadedDocumentRef = useRef(false);
  const reportRestoreInFlightRef = useRef(false);
  const downloadedSectionCatalogRef = useRef<any[] | null>(null);
  // Last reported adoption stage, for stage-aware failure messages.
  const adoptionProgressRef = useRef<{ label: string } | null>(null);

  const [sectionGeneration, setSectionGeneration] = useState(false);
  const [templateDocumentReadinessNotice, setTemplateDocumentReadinessNotice] =
    useState<DocumentReadinessNoticeData | null>(null);
  const [downloadContextNotice, setDownloadContextNotice] = useState<string | null>(null);
  const [downloadContextUnavailable, setDownloadContextUnavailable] = useState(false);
  // Post-adoption notice modal over a dimmed backdrop. User acknowledges
  // import reminders and preserved user edits and dismisses them cleanly.
  const [saveReminder, setSaveReminder] = useState<React.ReactNode | null>(null);
  const [saveNoticeTitle, setSaveNoticeTitle] = useState<string>("Import complete, be sure to save your document");
  const [saveNoticeConfirmText, setSaveNoticeConfirmText] = useState<string>("Got it");
  const [saveNoticeDismissed, setSaveNoticeDismissed] = useState(false);
  // Downloaded-DOCX adoption pass (distinct from AI generation). The pass runs
  // on its own full page between organization selection and MainPage, so these
  // two hold the real stage + section-level counts it reports from completed
  // work, plus the stage-qualified failure text for that page's error block.
  const [adoptionProgress, setAdoptionProgress] = useState<AdoptionProgress | null>(null);
  const [adoptionStageError, setAdoptionStageError] = useState<string | null>(null);
  // True from the moment we decide the report needs adopting until the pass
  // succeeds or the user leaves. Gates the page, suppresses MainPage, and
  // locks the header Back button for the duration of the Word run.
  const [adoptionPageActive, setAdoptionPageActive] = useState(false);
  // Per-section rows for the adoption page's list, computed once from the
  // sections actually handed to reparseReportDownload (post-filter), so the page
  // shows exactly the sections the pass is working on.
  const [adoptionSectionRows, setAdoptionSectionRows] = useState<AdoptionSectionProgress[]>([]);
  // Set when a downloaded report's project/template lists could not be loaded.
  // MainPage must not mount without them, so this replaces a blank pane with a
  // retryable message.
  const [mainDataError, setMainDataError] = useState<string | null>(null);

  const [showDone, setShowDone] = useState(false);
  const prevSectionGeneration = usePrevious(sectionGeneration);

  // Logger.debug("Home.tsx: Selected document id:", selectedReportTemplateId);

  const titleMap: Record<string, string> = {
    "RichApi.Error": "Word API Error",
    AxiosError: "Network Error",
    TypeError: "Unexpected Data Type",
    SyntaxError: "Internal Script Error",
    UNKNOWN_ERROR: "Unknown Error",
  };

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref.current;
  }

  const isSectionWorkActive = useCallback(
    (section: any) =>
      hasSectionActiveTask(section) ||
      Boolean(
        section?.chatAsyncInProgress ||
          section?.chatAsyncRegenerationInProgress ||
          section?.agenticInProgress ||
          section?.agenticRegenerationInProgress
      ),
    []
  );

  const sectionProgress = useMemo(
    () =>
      calculateSectionProgress({
        settingsGroup,
        sectionStatus,
        generatedSections,
        selectedSections,
        regeneratingSectionId: regenerateDialogContent?.section?.id,
        isSectionWorkActive,
      }),
    [
      generatedSections,
      isSectionWorkActive,
      regenerateDialogContent?.section?.id,
      sectionStatus,
      selectedSections,
      settingsGroup,
    ]
  );

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (prevSectionGeneration === true && sectionGeneration === false) {
      setShowDone(true);
      timer = setTimeout(() => {
        setShowDone(false);
      }, 1500);
    }

    return () => clearTimeout(timer);
  }, [sectionGeneration]);

  useEffect(() => {
    setTemplateDocumentReadinessNotice(null);
  }, [selectedProjectId]);

  const clearStates = () => {
    downloadedSectionCatalogRef.current = null;
    isDownloadedDocumentRef.current = false;
    setSelectsDisabled(false);
    setGeneratedSections([]);
    setSelectAllSections(false);
    setSelectedSections([]);
    setOutputDecision({});
    setSectionEvalStatus({});
    setSettingsGroup([]);
    setTemplateTagsMap({});
    setFeedbackDialogOpen(false);
    setRegenerateDialogOpen(false);
    setSourceDialogOpen(false);
    setSectionDialogContent({ section: {} });
    setFeedbackDialogContent(defaultFeedbackContent);
    setRegenerateDialogContent(defaultRegenerateContent);
    setSourceDialogContent([]);
    setShowSettings(false);
    setMessages(defaultChatMessage);
    setMessageChatId(null);
    setTemplateDocumentReadinessNotice(null);
    setDownloadContextNotice(null);
    setDownloadContextUnavailable(false);
    // Adoption is a per-document pass; signing out or switching organization
    // abandons whatever was on screen, so the page must not survive it.
    setAdoptionPageActive(false);
    setAdoptionProgress(null);
    setAdoptionStageError(null);
    setAdoptionSectionRows([]);
    adoptionProgressRef.current = null;
    setMainDataError(null);
    setShowOrganizationPicker(false);
  };

  const resetToSignIn = () => {
    setSignedIn(false);
    setShowOrganizationPicker(false);
    setDisplayMainFunc(false);
    setSelectedProjectId(null);
    setSelectedReportTemplateId(null);
    Logger.clearContext();
  };

  // Function to handle sign out and reset all states
  const handleSignOut = async () => {
    try {
      setFlushing(true);
      savingDisabledRef.current = true;
      Logger.flush();
      Logger.trace("Home.tsx: User logout initiated", { action: "logout", provider: authProvider });
      // Opening the add-in again must not sign them straight back in.
      markSignedOut(true);

      if (authProvider === "entra_external") {
        await entraLogout();
      } else {
        await jwtLogout();
      }
      clearStates();
      setTimeout(() => {
        resetToSignIn();
        setFlushing(false);
      }, 1000);
    } catch (error: any) {
      Logger.error("Home.tsx: Error during sign-out:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error?.name] || "Error",
        message: error?.name || "",
        details: {
          message: error?.message,
          code: error?.code,
          stack: error?.stack,
          context: {},
        },
      });
    }
  };

  // The session can also end without handleSignOut: when a refresh fails
  // (refresh token expired or revoked), AuthContext's interceptor logs out and
  // clears the tokens. Return to sign-in instead of leaving the signed-in pages
  // mounted with no session. `flushing` means handleSignOut is already tearing
  // down; it resets to sign-in itself.
  useEffect(() => {
    if (!signedIn || flushing || tokens.accessToken || tokens.refreshToken) return;
    Logger.info("Home.tsx: Session ended without sign-out; returning to sign-in");
    // As in handleSignOut: stop persisting before clearing, or the cleared
    // section state would be written over the document's saved state.
    savingDisabledRef.current = true;
    clearStates();
    resetToSignIn();
    setSignInNotice("Your session has ended. Please sign in again.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, flushing, tokens.accessToken, tokens.refreshToken]);

  // Word document related hooks
  const {
    trackChanges,
    handleTrackChangesToggle,
    insertTemplateDocument,
    formatSection,
    hasSection,
    hasSectionBookmarks,
    getAllBookmarks,
    insertTextAtSection,
    insertAtSection,
    scrollToSection,
    scrollToGroundingReference,
    createTable,
    acceptChanges,
    declineChanges,
    insertTextAtKey,
    getSelectedText,
    isSelectedText,
    insertTextAtSelection,
    insertImage,
  } = useWordDocument(setShowError);

  // Backend API related hooks
  const {
    retrieveKeyStatus,
    retrieveProjects,
    retrieveSettingsGroup,
    retrieveTags,
    retrieveLLMOuput,
    retrieveLLMOuputAsync,
    retrieveReportTemplates,
    retrieveBase64,
    retrievePlaceholders,
    retrieveChatHistory,
    retrieveChatEvaluation,
    retrieveChatComparison,
    retrieveChatAsyncTask,
    waitForChatAsyncResult,
    submitFeedback,
    retrievellmOuputWOGroup,
    retrievellmOuputWOGroupAsync,
    retrieveSourceDocument,
    retreiveSourceDocumentMetadata,
    uploadDocument,
    deleteDocument,
    deleteTag,
    transcribeAudio,
    synthesizeSpeech,
    retrieveAgentQuery,
    streamGetRequest,
    retrieveAgentStatus,
    retrieveAgentGrounding,
    processDocument,
    retrieveDocumentsList,
    retrieveDocumentsForImageInsertion,
    retrieveDocumentImages,
    downloadDocumentImage,
    submitLangfuseFeedback,
    logAuditAction,
    linkProjectToTemplate,
    getProjectChatHistory,
    getChatThread,
    setChatPinned,
    renameChatHistory,
    deleteChatHistory,
    startAgentQuery,
    getAgentTaskStatus,
    streamAgentTaskStatus,
    getModels,
    getProjectTags,
    getPromptTemplates,
  } = useBackendAPIs(baseUrl, tokens.accessToken || "", searchType);

  // Derive report_template_project_id from selectedProject's report_templates
  const reportTemplateProjectId = useMemo(() => {
    if (!selectedProjectId || !selectedReportTemplateId) return null;
    const project = projects.find((p) => p.id === selectedProjectId);
    const template = project?.report_templates?.find((t) => t.id === selectedReportTemplateId);
    return template?.report_template_project_id || null;
  }, [selectedProjectId, selectedReportTemplateId, projects]);

  // JWT handled via AuthContext provider

  /* Queue management for Word API calls */
  const saveQueueRef = useRef<Record<string, Promise<void>>>({});
  const lastWordRunRef = useRef<Promise<any>>(Promise.resolve());

  const debouncedSavesRef = useRef<Record<string, DebouncedFunc<(value: any) => void>>>({});

  const waitForAllSaves = async () => {
    const pending = Object.values(saveQueueRef.current);
    if (pending.length > 0) {
      Logger.debug("Home.tsx: Waiting for all saves to complete...");
      await Promise.allSettled(pending);
      Logger.debug("Home.tsx: All saves complete.");
    }
  };

  // Logger.debug("Home.tsx: Detect changes"); //Noisy

  const clearTemplateDocumentReadinessNotice = () => {
    setTemplateDocumentReadinessNotice(null);
  };

  const clearCustomProperties = async () => {
    await Word.run(async (context) => {
      const customProperties = context.document.properties.customProperties;
      customProperties.load("items");

      await context.sync();

      for (const prop of customProperties.items) {
        prop.delete();
      }

      await context.sync();
    });
  };

  const getStoredValue = async (item_name: string) => {
    return await Word.run(async (context) => {
      const props = context.document.properties.customProperties;
      props.load("items");
      await context.sync();

      if (item_name === "generatedSections") {
        Logger.debug("Home.tsx: Re-assembling splitted generated sections:", item_name);

        const assembledGeneratedSections = [];

        props.items.forEach((property) => {
          if (property.key.startsWith("section_")) {
            try {
              const sectionProperty = JSON.parse(property.value);
              assembledGeneratedSections.push(sectionProperty);
            } catch (error) {
              Logger.warn("Home.tsx: Error while re-assembling splitted generated sections", item_name);
            }
          }
        });

        return assembledGeneratedSections;
      } else if (item_name === "downloadDeletedSections") {
        return props.items
          .filter((property) => property.key.startsWith("jenlyDeleted_") && property.value === "true")
          .map((property) => property.key.slice("jenlyDeleted_".length));
      } else if (item_name === "sectionStatus") {
        const aggregate = props.items.find((property) => property.key === "sectionStatus");
        let result: Record<string, boolean> = {};
        if (aggregate) {
          try {
            result = JSON.parse(aggregate.value);
          } catch (error) {
            Logger.warn("Home.tsx: Invalid aggregate section status", error);
          }
        }
        props.items.forEach((property) => {
          if (!property.key.startsWith("sectionStatus_")) return;
          try {
            result[property.key.slice("sectionStatus_".length)] = JSON.parse(property.value);
          } catch (error) {
            Logger.warn("Home.tsx: Invalid per-section status", property.key);
          }
        });
        return result;
      } else {
        const property = props.items.find((p) => p.key === item_name);

        if (property) {
          try {
            const myData = JSON.parse(property.value);
            Logger.debug("Home.tsx:", item_name, "data found");
            return myData;
          } catch (error) {
            Logger.error(`Home.tsx: Json parsing failed for ${item_name}`, error);
          }
        } else {
          Logger.debug("Home.tsx: No saved data found for " + item_name);
          return null;
        }
      }
    });
  };

  function queuedWordRun<T>(callback: (context: Word.RequestContext) => Promise<T>): Promise<T> {
    const run = () =>
      Word.run(async (context) => {
        try {
          return await callback(context);
        } catch (err) {
          Logger.error("Home.tsx: Word.run error:", err);
          throw err;
        }
      });

    lastWordRunRef.current = lastWordRunRef.current.then(run, run);
    return lastWordRunRef.current;
  }

  const saveValue = async (item_name: string, data: any) => {
    if (savingDisabledRef.current) {
      Logger.debug(`Home.tsx: Saving disabled: skip saving ${item_name}`);
      return;
    }

    if (data === null || data === undefined) {
      Logger.debug(`Home.tsx: No data to save for ${item_name}. Skipping save.`);
      return;
    }

    const jsonString = JSON.stringify(data);

    const doSave = async () => {
      await queuedWordRun(async (context) => {
        const props = context.document.properties.customProperties;
        props.load("items");
        await context.sync();

        if (item_name === "sectionStatus" && isDownloadedDocumentRef.current) {
          Object.entries(data as Record<string, boolean>).forEach(([sectionId, included]) => {
            const key = `sectionStatus_${sectionId}`;
            const existing = props.items.find((property) => property.key === key);
            if (existing) existing.value = JSON.stringify(included);
            else props.add(key, JSON.stringify(included));
          });
        } else if (item_name === "generatedSections" && Array.isArray(data)) {
          Logger.debug("Home.tsx: Splitting generatedSections array into multiple sections");

          const currentSectionKeys = new Set(
            data.filter((section) => section?.id).map((section) => `section_${section.id}`)
          );

          props.items.forEach((property) => {
            if (property.key.startsWith("section_") && !currentSectionKeys.has(property.key)) {
              property.delete();
            }
          });

          if (isDownloadedDocumentRef.current) {
            (downloadedSectionCatalogRef.current || []).forEach((section) => {
              const key = `jenlyDeleted_${section.id}`;
              const existing = props.items.find((property) => property.key === key);
              if (currentSectionKeys.has(`section_${section.id}`)) {
                existing?.delete();
              } else if (!existing) {
                props.add(key, "true");
              }
            });
          }

          data.forEach((section) => {
            if (!section.id) return;

            const { content, snapshotGrounding, ...fullDataToStore } = section;
            const dataToStore = isDownloadedDocumentRef.current
              ? {
                  id: section.id,
                  name: section.name || section.display_name,
                  display_name: section.display_name || section.name,
                  outputDecisionStatus: section.outputDecisionStatus,
                  grounding_task_id: section.grounding_task_id,
                  chat_history_id: section.chat_history_id,
                  trace_url: section.trace_url || null,
                }
              : fullDataToStore;

            const sectionKey = `section_${section.id}`;
            const sectionProperty = JSON.stringify(dataToStore);

            const existingSectionProperty = props.items.find((property) => property.key === sectionKey);

            if (existingSectionProperty) {
              existingSectionProperty.value = sectionProperty;
            } else {
              props.add(sectionKey, sectionProperty);
            }
          });
        } else {
          const existingSectionProperty = props.items.find((property) => property.key === item_name);

          if (existingSectionProperty) {
            existingSectionProperty.value = jsonString;
          } else {
            // console.log("Saving to custom properties:", item_name, JSON.stringify(data, null, 2));
            props.add(item_name, jsonString);
          }
        }

        await context.sync();
        // console.log(`${item_name} saved.`);
      });
    };

    // Add to save queue to avoid overlapping writes
    if (!saveQueueRef.current[item_name]) {
      saveQueueRef.current[item_name] = doSave();
    } else {
      saveQueueRef.current[item_name] = saveQueueRef.current[item_name].then(doSave, doSave);
    }

    return saveQueueRef.current[item_name];
  };

  function debouncedSave(key: string) {
    if (savingDisabledRef.current) {
      debouncedSavesRef.current[key]?.cancel();
      return (_value: any) => undefined;
    }
    if (!debouncedSavesRef.current[key]) {
      debouncedSavesRef.current[key] = debounce((value: any) => {
        if (!savingDisabledRef.current) {
          Logger.debug("Home.tsx: debouncedSave:", key);
          saveValue(key, value);
        } else {
          Logger.debug(`Home.tsx: Saving disabled: skip debounced save for ${key}`);
        }
      }, 500);
    }
    return debouncedSavesRef.current[key];
  }

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     checkSelection();
  //   }, 2000);

  //   return () => clearInterval(interval);
  // }, []);

  const generateAll = async (_section: any) => {
    // Intentionally a no-op: this flow deliberately runs no per-entry quality checks.
  };

  const changeEnvironment = async (value) => {
    setSelectedEnvironmentById(value);

    clearStates();
    setDisplayMainFunc(false);
    setSelectedProjectId(null);
    setSelectedReportTemplateId(null);
    setProjects([]);

    const fetchProjects = async () => {
      if (!tokens.accessToken) return;
      const projectList = await retrieveProjects();
      setProjects(projectList);
    };

    clearCustomProperties();
    fetchProjects();

    // Audit trail: log UPDATE action for environment change
    if (reportTemplateProjectId) {
      logAuditAction(reportTemplateProjectId, "UPDATE", `Environment: ${value}`);
    }

    Logger.debug("Home.tsx: Switched Environments");
  };

  const handleSearchTypeChange = (value: string) => {
    setSearchType(value);
    // Audit trail: log UPDATE action for search type change
    if (reportTemplateProjectId) {
      logAuditAction(reportTemplateProjectId, "UPDATE", `Search Type: ${value}`);
    }
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!tokens.accessToken || !signedIn) return;

    const fetchProjects = async () => {
      try {
        const projectList = await retrieveProjects();
        setProjects(projectList);
      } catch (error) {
        // retrieveProjects rethrows. Unhandled, this silently stranded
        // projects at [] for the rest of the session — every consumer then
        // looked up a project that was not there.
        Logger.error("Home.tsx: retrieveProjects failed", error);
        setMainDataError("Jenly could not load your projects.");
      }
    };

    fetchProjects();
  }, [tokens.accessToken, organizationId, signedIn]);

  useEffect(() => {
    if (selectedProjectId) {
      const project = projects.find((p) => p.id === selectedProjectId);
      Logger.trace("Home.tsx: Project selected", {
        action: "project_selection",
        projectId: selectedProjectId,
        projectName: project?.name,
      });
      Logger.setContext({ projectId: selectedProjectId, projectName: project?.name || "" });
      fetchReportTemplates(selectedProjectId);
    }
  }, [selectedProjectId, projects]);

  useEffect(() => {
    if (selectedReportTemplateId) {
      const doc = reportTemplates.find((t) => t.id === selectedReportTemplateId);
      Logger.trace("Home.tsx: Report template selected", {
        action: "report_selection",
        reportId: selectedReportTemplateId,
        reportName: doc?.name,
      });
      fetchSettingsGroup(selectedReportTemplateId);
    }
  }, [selectedReportTemplateId]);

  useEffect(() => {
    Logger.debug("Home.tsx: generatedSections updated, count:", generatedSections.length);
    generatedSections.forEach((section) => {
      if (!section.evalStatus) {
        setSectionEvalStatus((prev) => ({
          ...prev,
          [section.id]: {
            multimodel: { status: "uninitialized", data: null },
          },
        }));

        section.evalStatus = { status: "uninitialized", data: null };
      }
    });
    if (isDownloadedDocumentRef.current && !savingDisabledRef.current) {
      void saveValue("generatedSections", generatedSections).catch((error) =>
        Logger.error("Home.tsx: Could not save downloaded section review state", error)
      );
    } else {
      debouncedSave("generatedSections")(generatedSections);
    }
  }, [generatedSections]);

  useEffect(() => {
    setGeneratedSections((prevSections) =>
      prevSections.map((section) => {
        const sectionEvalStatusContent = sectionEvalStatus[section.id];
        if (sectionEvalStatusContent) {
          return { ...section, evalStatus: sectionEvalStatusContent.multimodel };
        }
        return section;
      })
    );
  }, [sectionEvalStatus]);

  useEffect(() => {
    if (isDownloadedDocumentRef.current && !savingDisabledRef.current) {
      void saveValue("sectionStatus", sectionStatus).catch((error) =>
        Logger.error("Home.tsx: Could not save downloaded section status", error)
      );
    } else {
      debouncedSave("sectionStatus")(sectionStatus);
    }
  }, [sectionStatus]);

  useEffect(() => {
    setGeneratedSections((prevSections) =>
      prevSections.map((section) => {
        const outputDecisionStatus = outputDecision[section.id];
        return { ...section, outputDecisionStatus };
      })
    );
  }, [outputDecision]);

  useEffect(() => {
    debouncedSave("uploadedFiles")(uploadedFiles);
  }, [uploadedFiles]);

  useEffect(() => {
    Logger.debug("Home.tsx: Regenerate Dialog Updated:", regenerateDialogOpen);
  }, [regenerateDialogOpen]);

  useEffect(() => {
    async function getReportPlaceholders() {
      try {
        const templatePlaceholders = await retrievePlaceholders(selectedReportTemplateId);
        setProjectPlaceholderDetails(templatePlaceholders);
      } catch (error) {
        Logger.error("Home.tsx: Project error details loading failed:", error);
      }
    }
    if (selectedReportTemplateId) getReportPlaceholders();
  }, [selectedReportTemplateId]);

  /*  Function definitions for fetching data and managing state */

  const fetchReportTemplates = async (project_id: string) => {
    // retrieveReportTemplates rethrows on failure. Without this, a 401 during
    // the post-org-switch window leaves reportTemplates at [] and the template
    // can never be resolved.
    const reportTemplatesList = await retrieveReportTemplates(project_id);
    setReportTemplates(reportTemplatesList);
    setSelectedReportTemplateId((currentTemplateId) => {
      if (currentTemplateId && reportTemplatesList.some((template) => template.id === currentTemplateId)) {
        return currentTemplateId;
      }

      return reportTemplatesList[0]?.id ?? null;
    });
  };

  /**
   * Load the project + report templates a downloaded report depends on, and
   * wait for them, before MainPage is allowed to mount.
   *
   * `projects` is only fetched by the effect keyed on `signedIn`, which is
   * correct for normal documents but leaves a downloaded report depending on
   * effect timing. Loading both here, awaited, makes the hand-off to MainPage
   * deterministic instead of a race.
   *
   * Returns whether the document's project and template are actually usable.
   * A plain "loaded successfully" is not enough: the fetch can succeed while
   * the project is absent from the list (revoked access, wrong organization),
   * and without a verdict the readiness gate would spin forever.
   */
  const ensureDownloadDocumentData = useCallback(
    async (projectId: string | null, templateId: string | null): Promise<boolean> => {
      if (!projectId || !templateId) {
        setMainDataError("Jenly could not read this report's project details.");
        return false;
      }
      setMainDataError(null);
      try {
        let projectList = projects;
        if (!projectList.some((project: any) => project.id === projectId)) {
          projectList = await retrieveProjects();
          setProjects(projectList);
        }
        const templateList = await retrieveReportTemplates(projectId);
        setReportTemplates(templateList);
        setSelectedProjectId(projectId);
        if (templateId && templateList.some((template: any) => template.id === templateId)) {
          setSelectedReportTemplateId(templateId);
        } else {
          setSelectedReportTemplateId((currentTemplateId) => {
            if (currentTemplateId && templateList.some((template: any) => template.id === currentTemplateId)) {
              return currentTemplateId;
            }
            return templateList[0]?.id ?? null;
          });
        }

        const ready =
          projectList.some((project: any) => project.id === projectId) &&
          templateList.some((template: any) => template.id === templateId);
        if (!ready) {
          setMainDataError("Jenly could not find this report's project and template.");
        }
        return ready;
      } catch (error) {
        Logger.error("Home.tsx: Download document data load failed", error);
        setMainDataError("Jenly could not load this report's details.");
        return false;
      }
    },
    [projects, retrieveProjects, retrieveReportTemplates],
  );

  const fetchSettingsGroup = async (report_template_id: string) => {
    let currentSettingsGroup = [];
    try {
      currentSettingsGroup = await retrieveSettingsGroup(report_template_id);
    } catch (error) {
      if (!downloadedSectionCatalogRef.current) throw error;
      Logger.warn("Home.tsx: Using frozen section catalog because the template is unavailable", error);
    }
    const settingsGroupList = downloadedSectionCatalogRef.current
      ? buildDownloadedSectionList(downloadedSectionCatalogRef.current, currentSettingsGroup)
      : currentSettingsGroup;
    setSettingsGroup(settingsGroupList);

    try {
      const retrievedTags = await retrieveTags({ report_template_id, per_page: 1000 });
      const nextTemplateTagsMap = retrievedTags.reduce((acc: Record<string, TemplateTag>, tag: any) => {
        acc[tag.id] = {
          id: tag.id,
          name: tag.name,
          color: tag.color || "#86868b",
          description: tag.description,
        };
        return acc;
      }, {});

      setTemplateTagsMap(nextTemplateTagsMap);
    } catch (error) {
      Logger.error("Home.tsx: Failed to retrieve template tags", error);
      setTemplateTagsMap({});
    }

    return settingsGroupList;
  };

  const fetchBookmarks = async () => {
    // A Word failure here used to reject into handleSignIn's catch and abort
    // the whole sign-in, leaving the pane half-set-up.
    try {
      const bookmarks = await getAllBookmarks();
      setBookmarks(bookmarks);
    } catch (error) {
      Logger.error("Home.tsx: fetchBookmarks failed", error);
      setBookmarks([]);
    }
  };

  const disableAllSelects = (bool: boolean) => {
    setSelectsDisabled(bool);
  };

  const formatDateToLongForm = (dateString) => {
    if (!dateString || typeof dateString !== "string") return "";

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const [year, month, day] = dateString.split("-");
    const monthIndex = parseInt(month, 10) - 1;

    if (isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11 || !year || !day) {
      return dateString;
    }

    const monthName = months[monthIndex];
    return `${parseInt(day, 10)}-${monthName}-${year}`;
  };

  const runTask = async (
    type: "multimodel" | "output" | "paraphrase" | "confidence",
    sectionId: string,
    chat_history_id: string,
    interval = 1000
  ) => {
    const poll = async () => {
      try {
        let response;
        let isCompleted = false;
        if (type === "multimodel") {
          Logger.debug(`Home.tsx: Polling for multimodel comparison on section ${sectionId}...`);
          response = await retrieveChatComparison(chat_history_id);
          isCompleted = response.comparison_report !== null;
        } else {
          response = await retrieveChatEvaluation(chat_history_id);
          isCompleted = response.evaluation_report.status === "completed";
        }

        setSectionEvalStatus((prev) => ({
          ...prev,
          [sectionId]: {
            ...prev[sectionId],
            [type]: {
              status: isCompleted ? "completed" : "pending",
              data: !isCompleted
                ? null
                : type === "multimodel"
                ? { score: response.comparison_report.similarity_score, reason: response.comparison_report.reason }
                : { score: response.evaluation_report.score },
            },
          },
        }));

        setSectionStatus((prev) => ({ ...prev, [sectionId]: true }));

        if (!isCompleted) {
          setTimeout(poll, interval);
        }
      } catch (error) {
        Logger.error(`Home.tsx: Polling error for ${type} on section ${sectionId}:`, error);
        if (type === "multimodel") {
          setSectionEvalStatus((prev) => ({
            ...prev,
            [sectionId]: {
              ...prev[sectionId],
              [type]: {
                status: "unavailable",
                data: null,
              },
            },
          }));
        }
      }
    };

    poll();
  };

  const checkSelection = async () => {
    const isSelected = await isSelectedText(); // your existing function
    setSelectedText(!isSelected || false);
  };

  const checkSectionsStatus = async (currentSettingsGroup) => {
    const checks = await Promise.all(
      currentSettingsGroup
        .filter((section) => section.type?.toLowerCase() === "section")
        .map(async (section) => {
          const exists = await hasSection(section.insertion_key);
          return [section.id, exists] as const;
        })
    );

    const statusMap = Object.fromEntries(checks);
    setSectionStatus(statusMap);
  };

  const toggleRegenerateOptions = (name: string) => {
    setRegenerateDialogContent((prev) => {
      const isSelected = prev.options.includes(name);
      const updatedOptions = isSelected ? prev.options.filter((opt) => opt !== name) : [...prev.options, name];

      return {
        ...prev,
        options: updatedOptions,
      };
    });
  };

  const handleSettingsToggle = () => {
    setShowSettings(!showSettings);
  };

  const handleClearProperties = async () => {
    clearCustomProperties();
    // Audit trail: log DELETE action for clearing custom properties
    if (reportTemplateProjectId) {
      logAuditAction(reportTemplateProjectId, "DELETE", "Custom Properties Cleared");
    }
  };

  const handleSignIn = useCallback(async () => {
    if (reportRestoreInFlightRef.current) return;
    reportRestoreInFlightRef.current = true;
    let renderingFailed = false;
    try {
      savingDisabledRef.current = true;
      Object.values(debouncedSavesRef.current).forEach((save) => save.cancel());
      await waitForAllSaves();
      Logger.setAxios(authAxios);

      const projId = await getStoredValue("selectedProjectId");

      const docId = await getStoredValue("selectedReportTemplateId");

      const docOrgId = await getStoredValue("selectedOrganizationId");
      if (
        docOrgId &&
        organizationId &&
        String(organizationId).toLowerCase() !== String(docOrgId).toLowerCase()
      ) {
        try {
          await switchOrganization(String(docOrgId), tokens.accessToken || "");
        } catch (error) {
          Logger.warn("Home.tsx: Failed to auto-switch organization to match document", error);
        }
      }

      const localSections = await getStoredValue("generatedSections");
      let sections = localSections;
      const sectionStatus = await getStoredValue("sectionStatus");
      const deletedSections = await getStoredValue("downloadDeletedSections");
      const downloadId = await getStoredValue("jenlyReportDownloadId");
      const runId = await getStoredValue("jenlyReportRunId");
      const downloadVersion = await getStoredValue("jenlyDownloadSchemaVersion");
      isDownloadedDocumentRef.current = Boolean(downloadId);
      let canSaveDocumentProperties = !downloadId;
      if (downloadId && downloadVersion !== "2") {
        setDownloadContextUnavailable(true);
        setDownloadContextNotice("This report remains readable in Word. Update the Jenly add-in to restore section review, sources, and grounding.");
        sections = [];
      }
      if (downloadId && downloadVersion === "2") {
        try {
          // Check the selected organization's project/template access before Word writes.
          if (!await ensureDownloadDocumentData(projId, docId)) {
            throw new Error("This report's project and template are unavailable in the selected organization.");
          }
          const expected = { download_id: downloadId, run_id: runId, project_id: projId, template_id: docId };
          const payload = await loadReportDownloadSource(expected, async () =>
            (await authAxios.get(`/reports/downloads/${encodeURIComponent(downloadId)}/addin-context`)).data);
          const frozen = { ...payload, version: 2 };
          canSaveDocumentProperties = true;
          sections = hydrateReportDownloadSections(frozen.sections, sections || [], sectionStatus || {}, deletedSections || []);
          const frozenById = new Map(frozen.sections.map((section) => [section.settings_group_id, section]));
          sections = await Promise.all(sections.map(async (section) => {
            const original: any = frozenById.get(section.id);
            if (!original || section.type?.toLowerCase() !== "section" ||
                !section.chat_history_id || section.chat_history_id === original.chat_history_id) return section;
            try {
              const localChat = await retrieveChatHistory(section.chat_history_id);
              return {
                ...section,
                content: localChat.answer || section.content,
                sources: localChat.sources || [],
              };
            } catch (error) {
              Logger.warn("Home.tsx: Regenerated section sources are unavailable", error);
              return section;
            }
          }));
          const localById = new Map((localSections || []).map((section) => [section.id, section]));
          const adoptionSections = frozen.sections.filter((section) => {
            if (sectionStatus?.[section.settings_group_id] === false) return false;
            if (deletedSections?.includes(section.settings_group_id)) return false;
            const local: any = localById.get(section.settings_group_id);
            if (local && Object.prototype.hasOwnProperty.call(local, "grounding_task_id") &&
                local.grounding_task_id !== section.agent_task_id) return false;
            if (local && Object.prototype.hasOwnProperty.call(local, "chat_history_id") &&
                local.chat_history_id !== section.chat_history_id) return false;
            return true;
          });
          downloadedSectionCatalogRef.current = frozen.sections.map((section) => ({
            id: section.settings_group_id,
            name: section.name || section.display_name,
            insertion_key: section.insertion_key,
            display_name: section.display_name,
            type: section.section_type,
            user_query: section.user_query,
            is_enabled: true,
            fromReportDownload: true,
          }));
          setSettingsGroup(downloadedSectionCatalogRef.current);
          await fetchSettingsGroup(docId);
          setDownloadContextUnavailable(false);
          setDownloadContextNotice(null);
          setAdoptionStageError(null);
          setSaveReminder(null);
          setSaveNoticeTitle("Import complete, be sure to save your document");
          setSaveNoticeConfirmText("Got it");
          setSaveNoticeDismissed(false);
          adoptionProgressRef.current = null;
          setAdoptionSectionRows(buildAdoptionSectionRows(adoptionSections));
          // Probe before rendering anything: an already-adopted report (every
          // reopen after a successful pass) has nothing to show, so it must
          // not flash a 100% page. The probe is read-only — it loads custom
          // properties and content controls and writes nothing.
          const alreadyAdopted = await probeReportRender(adoptionSections);
          if (!alreadyAdopted) {
            // Show the adoption page by flipping adoptionPageActive ONLY.
            // Deliberately do NOT set signedIn / showOrganizationPicker /
            // displayMainFunc here: `signedIn` gates the effect that fetches
            // projects (keyed on signedIn), and flipping it during adoption
            // moves that fetch into the post-org-switch token window where it
            // can 401. The interceptor recovers a 401 by logging out, which
            // clears tokens and strands projects/reportTemplates empty — and
            // MainPage then dereferences an undefined report template.
            // The page is gated on adoptionPageActive alone, so it needs no
            // help from the auth state to be visible.
            setAdoptionProgress(null);
            setAdoptionPageActive(true);
          }
          try {
            const result = alreadyAdopted
              ? { changed: false, preserved: [], warnings: [], skippedCitationSections: [] as string[] }
              : await reparseReportDownload({
              payload, sections: adoptionSections,
              services: { retrieveDocumentsForImageInsertion, retrieveDocumentImages, downloadDocumentImage },
              reviewedIds: (localSections || []).filter((section) => section.outputDecisionStatus === false).map((section) => section.id),
              onProgress: (progress) => {
                adoptionProgressRef.current = { label: progress.label };
                setAdoptionProgress(progress);
              },
            });
            // The adoption surface retains its completed progress state until
            // the page hands off to the main view after post-import tasks.
            try {
              let shouldGenerateTodoComments = shouldGenerateWordComment("JENLY_GENERATE_COMMENTS");
              await Word.run(async (context) => {
                await wordTask(context, "TODO:", null, true, shouldGenerateTodoComments);
              });
              await Word.run(async (context) => {
                await wordTask(context, "information not available in the provided context", null, true);
              });
            } catch (todoError) {
              Logger.warn("Home.tsx: Reparse TODO styling pass failed", todoError);
            }
            void ensureTaskpaneAutoOpens();
            const skippedCount = result.skippedCitationSections?.length ?? 0;
            const editsText = skippedCount > 0
              ? `Your edits were kept in ${skippedCount} section${skippedCount === 1 ? "" : "s"}; citation links inside the edited text were not re-created.`
              : null;
            const saveText = result.changed
              ? "Your report import is complete! Be sure to save your document so all imported sections, citations, and formatting are preserved when you reopen it."
              : null;

            if (editsText) {
              setSaveNoticeTitle("Section edits preserved");
              setSaveNoticeConfirmText("Accept and close");
              setSaveReminder(
                saveText ? (
                  <>
                    <p style={{ margin: "0 0 0.5rem" }}>{editsText}</p>
                    <p style={{ margin: 0 }}>{saveText}</p>
                  </>
                ) : (
                  editsText
                ),
              );
            } else if (saveText) {
              setSaveNoticeTitle("Import complete, be sure to save your document");
              setSaveNoticeConfirmText("Got it");
              setSaveReminder(saveText);
            } else if (result.warnings.length) {
              setSaveNoticeTitle("Report import notice");
              setSaveNoticeConfirmText("Got it");
              setSaveReminder("Some existing citation links could not be restored.");
            }
            // An already-adopted document (adopted === false) never entered the
            // page and just falls through to the normal synced pane.
          } catch (adoptionError) {
            renderingFailed = true;
            // Stay on the page: it owns the inline stage-qualified error and
            // the Retry affordance now, preserving last reported progress.
            const stage = adoptionProgressRef.current?.label;
            const detail = adoptionError instanceof Error ? adoptionError.message : String(adoptionError);
            Logger.warn("Home.tsx: Download adoption incomplete", adoptionError);
            setAdoptionStageError(stage ? `${stage} failed. ${detail}` : detail);
          }
        } catch (error) {
          Logger.warn("Home.tsx: Download context unavailable", error);
          canSaveDocumentProperties = false;
          setDownloadContextUnavailable(true);
          // If the page was already up (a throw after activation), tear it down
          // so the user lands on the normal unavailable flow with its Retry,
          // rather than a progress page that can no longer make progress.
          setAdoptionPageActive(false);
          setAdoptionProgress(null);
          sections = [];
          downloadedSectionCatalogRef.current = null;
          setDownloadContextNotice(
            `This report remains readable in Word. Section review, sources, and grounding could not be restored. ${error instanceof Error ? error.message : "Retry when its download context is available."}`
          );
        }
      }
      if (sections) setGeneratedSections(sections);

      if (sections) {
        const evalStatus = sections.reduce((initialEvalStatus, section) => {
          initialEvalStatus[section.id] = {
            multimodel: section.evalStatus,
          };
          return initialEvalStatus;
        }, {});

        if (evalStatus) setSectionEvalStatus(evalStatus);

        const outputDecision = sections.reduce((initialOutputDecision, section) => {
          initialOutputDecision[section.id] = section.outputDecisionStatus;
          return initialOutputDecision;
        }, {});

        if (outputDecision) setOutputDecision(outputDecision);
      }

      if (sectionStatus) setSectionStatus(sectionStatus);

      const files = await getStoredValue("uploadedFiles");
      if (files) setUploadedFiles(files);

      if (projId) setSelectedProjectId(projId);
      if (docId) setSelectedReportTemplateId(docId);

      // Downloaded documents have their project and template baked into the
      // doc properties, so this is the only path that mounts MainPage during
      // sign-in — and MainPage cannot render without projects/reportTemplates
      // (ProjectDetails dereferences the resolved template). Load them here,
      // awaited, instead of relying on the effects racing the mount.

      if (projId && docId && (!downloadId || canSaveDocumentProperties)) {
        await fetchBookmarks();
        setDisplayMainFunc(true);
      } else if (downloadId) {
        setDisplayMainFunc(false);
      }

      // A completed sign-in always lands on the project picker/home flow.
      setShowOrganizationPicker(false);
      setSignedIn(true);
      setSignInNotice(null);
      // Torn down in the SAME batch as displayMainFunc/signedIn above, so the
      // adoption page hands off to MainPage in one commit and the auth page
      // never flashes back in between.
      if (!renderingFailed) {
        setAdoptionPageActive(false);
        setAdoptionProgress(null);
      }
      savingDisabledRef.current = renderingFailed || !canSaveDocumentProperties;
      if (!renderingFailed && canSaveDocumentProperties && isDownloadedDocumentRef.current && sections) {
        void saveValue("generatedSections", sections).catch((error) =>
          Logger.error("Home.tsx: Could not save downloaded section review state after adoption", error)
        );
      }
    } catch (error) {
      Logger.error("Home.tsx: Error during sign-in:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: { error },
        },
      });
    } finally {
      reportRestoreInFlightRef.current = false;
    }
  }, [authAxios, retrieveDocumentsForImageInsertion, retrieveDocumentImages, downloadDocumentImage, setSignedIn]);

  const handleBackToOrganizations = useCallback(() => {
    setAdoptionPageActive(false);
    setShowOrganizationPicker(true);
  }, []);

  const handleOrganizationChanged = useCallback(() => {
    clearStates();
    setDisplayMainFunc(false);
    setSelectedProjectId(null);
    setSelectedReportTemplateId(null);
    setProjects([]);
    setReportTemplates([]);
    setShowOrganizationPicker(false);
  }, []);

  const handleTemplateGeneration = async () => {
    try {
      downloadedSectionCatalogRef.current = null;
      if (!selectedProjectId || !selectedReportTemplateId) return;

      clearTemplateDocumentReadinessNotice();

      const selectedProject = projects.find((p) => p.id === selectedProjectId);
      const selectedDoc = reportTemplates.find((t) => t.id === selectedReportTemplateId);
      Logger.trace("Home.tsx: Report generation started", {
        action: "report_generation",
        projectId: selectedProjectId,
        projectName: selectedProject?.name,
        reportId: selectedReportTemplateId,
        reportName: selectedDoc?.name,
      });
      setSectionGeneration(true);

      await waitForAllSaves();

      disableAllSelects(true);

      const projectDocumentsResponse = await retrieveDocumentsList({ projectId: selectedProjectId, perPage: 100 });
      const readinessNotice = getDocumentsNotReadyNoticeFromDocuments(projectDocumentsResponse?.items || []);

      if (readinessNotice) {
        setTemplateDocumentReadinessNotice(readinessNotice);
        disableAllSelects(false);
        setSectionGeneration(false);
        return;
      }

      const updatedProjects = projects.map((project) => {
        if (project.id === selectedProjectId) {
          return {
            ...project,
            data_lock_date: formatDateToLongForm(project.data_lock_date),
            start_reporting_date: formatDateToLongForm(project.start_reporting_date),
            end_reporting_date: formatDateToLongForm(project.end_reporting_date),
          };
        }
        return project;
      });
      setProjects(updatedProjects);

      const templatePromise = retrieveBase64(
        reportTemplates.find((template) => template.id === selectedReportTemplateId)
      );

      const project = updatedProjects.find((p) => p.id === selectedProjectId);

      let currentSettingsGroup = settingsGroup;

      if (currentSettingsGroup.length === 0) {
        Logger.debug("Home.tsx: settingsGroup state was empty. Fetching now...");
        currentSettingsGroup = await fetchSettingsGroup(selectedReportTemplateId);
      }

      Logger.debug("Home.tsx: Inspecting settingsGroup before filter, count:", currentSettingsGroup.length);

      const llmProcessingPromise = Promise.all(
        currentSettingsGroup
          .filter(
            (section) => section.type.toLowerCase() === "base" || section.type.toLowerCase() === "project_settings"
          )
          .map(async (section) => {
            let output;
            if (section.display_name.toLowerCase() === "static") {
              if (section.user_query in project && section.user_query === "active_substance_name") {
                const val = project[section.user_query];
                output = val.charAt(0).toUpperCase() + val.slice(1);
              } else if (section.user_query in project) {
                output = project[section.user_query];
              }
            } else {
              try {
                const llmResponse = await retrieveLLMOuput(selectedProjectId, section.id);
                output = llmResponse.answer;
              } catch (error) {
                Logger.error(
                  `Home.tsx: LLM response error during template generation for section: ${section.question}`,
                  error
                );
              }
            }
            return { insertion_key: section.insertion_key, display_name: section.display_name, output };
          })
      );

      const template = await templatePromise;
      await insertTemplateDocument(template.file_data);
      void ensureTaskpaneAutoOpens();
      await saveValue("selectedProjectId", selectedProjectId);
      await saveValue("selectedReportTemplateId", selectedReportTemplateId);
      if (organizationId) {
        await saveValue("selectedOrganizationId", organizationId);
      }

      let shouldGenerateTodoComments = shouldGenerateWordComment("JENLY_GENERATE_COMMENTS");

      // Format template TODO items in red-orange immediately after inserting the report template
      await Word.run(async (context) => {
        await wordTask(context, "TODO:", null, true, shouldGenerateTodoComments);
      });

      const resolvedOutputs = await llmProcessingPromise;

      try {
        await insertTextAtKey(resolvedOutputs);
      } catch (error) {
        Logger.error(`Home.tsx: Inserting texts at insertion keys failed`, error);
      }

      await Word.run(async (context) => {
        await wordTask(context, "TODO:", null, true, shouldGenerateTodoComments);
      });

      await Word.run(async (context) => {
        await wordTask(context, "information not available in the provided context", null, true);
      });

      await fetchBookmarks();

      await new Promise((resolve) => setTimeout(resolve, 1000));
      await checkSectionsStatus(currentSettingsGroup);

      Logger.debug("Home.tsx: Settings Group count:", currentSettingsGroup.length);

      setDisplayMainFunc(true);
      disableAllSelects(false);
      setSectionGeneration(false);

      // Audit trail: log CREATE action for template generation
      if (reportTemplateProjectId) {
        logAuditAction(reportTemplateProjectId, "CREATE");
      }
    } catch (error) {
      Logger.error("Home.tsx: Error during template generation:", error);
      disableAllSelects(false);
      setSectionGeneration(false);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context: axios.isAxiosError(error)
            ? {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                statusText: error.response?.statusText,
                responseData: error.response?.data?.detail,
              }
            : error.name === "RichApi.Error"
            ? {
                errorLocation: error.debugInfo?.errorLocation,
                statement: error.debugInfo?.statement,
                surroundingStatements: error.debugInfo?.surroundingStatements,
              }
            : undefined,
        },
      });
    }
  };

  // Derived, so no code path can mount MainPage without the project/template
    // data ProjectDetails dereferences during render.
    const mainDataReady = useMemo(
      () =>
        isMainDataReady({
          projects,
          reportTemplates,
          projectId: selectedProjectId,
          templateId: selectedReportTemplateId,
          isDownloadedDocument: Boolean(isDownloadedDocumentRef.current),
        }),
      [projects, reportTemplates, selectedProjectId, selectedReportTemplateId],
    );
    // Single source of truth for which page the content area shows. Replaces
    // a chain of inline conditions in the render tree, two of which had been
    // independently inverted — each presenting as a completely blank pane.
    const homePage = resolveHomePage({
      signedIn,
      showSettings,
      showOrganizationPicker,
      displayMainFunc,
      adoptionPageActive,
      isDownloadedDocument: Boolean(isDownloadedDocumentRef.current),
      mainDataReady,
      hasMainDataError: Boolean(mainDataError),
    });
    const showMainDataPlaceholder = homePage === "loading" || homePage === "error";

    // Show Back while the adoption page is up, and on the placeholder/error
    // state, so the user is never stranded. Locked only while the pass runs.
    const showBackButton =
      !showSettings &&
      (adoptionPageActive || (signedIn && !showOrganizationPicker && !displayMainFunc) || showMainDataPlaceholder);

    // Logger.debug("Home.tsx: showError state:", showError?.isVisible);  //Noisy

  // The page mounts before reparseReportDownload's first report lands (it yields
  // at context.sync() boundaries), so seed the inspect stage to avoid a blank
  // content area on that first frame.
  const initialAdoptionProgress = useMemo<AdoptionProgress>(
    () => ({
      stage: "inspect",
      label: adoptionStageLabel("inspect"),
      processed: 0,
      total: 1,
      sectionsTotal: adoptionSectionRows.length,
      sectionsDone: 0,
      sectionsComplete: [],
      // reparseReportDownload reports the real grand total once it knows the
      // marker count; until then there is no work to show, so 0 of 0.
      unitsDone: 0,
      unitsTotal: 0,
      sectionIndex: null,
      sectionName: null,
      sectionMarkersDone: 0,
      sectionMarkersTotal: 0,
    }),
    [adoptionSectionRows.length],
  );

  return (
    <div className="wrapper">
      <Header>
        {showSettings && (
          <div className="header_icon" onClick={handleSettingsToggle}>
            <LeftOutlined />
            <span>Back</span>
          </div>
        )}
        {showBackButton && (
          <Tooltip
            title={
              adoptionPageActive && !adoptionStageError
                ? "Jenly is linking this report's sources. Stay on this page until it finishes."
                : undefined
            }
          >
            {/* Locked only while the pass is actually running: leaving mid-run
                would abandon a half-adopted document. Once it fails, Back is
                free again so the user is never trapped. */}
            <button
              type="button"
              className="header_project_back"
              onClick={handleBackToOrganizations}
              disabled={adoptionPageActive && !adoptionStageError}
              aria-label="Back to organization selection"
            >
              <LeftOutlined aria-hidden="true" />
              <span>Back</span>
            </button>
          </Tooltip>
        )}
        {signedIn && !showSettings && !showOrganizationPicker && displayMainFunc && (
          <div className="section-progress-header-slot">
            {sectionProgress.visible && (
              <Tooltip
                title={
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "2px 0" }}>
                    <span style={{ fontWeight: 600 }}>Generation Progress</span>
                    <span>• Running: {sectionProgress.running}</span>
                    <span>• Completed: {sectionProgress.completed} of {sectionProgress.total}</span>
                    <span>• Remaining: {sectionProgress.remaining}</span>
                    <span>• {sectionProgress.percent}% completed</span>
                  </div>
                }
              >
                <div
                  className="section-progress-header"
                  aria-label={`Generation progress: ${sectionProgress.completed} of ${sectionProgress.total} completed, ${sectionProgress.remaining} remaining`}
                >
                  <Progress
                    type="circle"
                    percent={sectionProgress.percent}
                    size={22}
                    strokeWidth={10}
                    status={sectionProgress.remaining === 0 && sectionProgress.total > 0 ? "success" : "normal"}
                    strokeColor="var(--primary-color)"
                    showInfo={false}
                  />
                  <span className="section-progress-header-count">
                    {sectionProgress.completed}/{sectionProgress.total}
                  </span>
                </div>
              </Tooltip>
            )}
          </div>
        )}
        <div></div>
        <span className="header_title header_version_title">{`Version ${VERSION} ${
          selectedEnvironment?.id === "prod" ? "" : `(${selectedEnvironment?.id})`
        }`}</span>
        <div></div>
        <div></div>
        {!signedIn && showSettings && <div style={{ width: "75px" }}></div>}
      </Header>
      <div className="content" style={{ position: "relative" }}>
        {/* Downloaded-DOCX adoption pass: its own page, rendered below, so the
            org picker is gone by the time any Word write happens. */}
          {homePage === "adoption" && (
          <ReportAdoptionPage
            progress={adoptionProgress ?? initialAdoptionProgress}
            sections={adoptionSectionRows}
            error={adoptionStageError}
            canRetry={Boolean(adoptionStageError)}
            onRetry={handleSignIn}
          />
        )}
        {/* Context-level notices only. Adoption failures are reported on the
            adoption page itself, so suppress them here to avoid saying it twice. */}
        {downloadContextNotice && !adoptionPageActive && (
          <div role="status" style={{ padding: "8px 12px", background: "#fff7e6" }}>
            {downloadContextNotice}
            {downloadContextUnavailable && signedIn && (
              <button type="button" onClick={handleSignIn} style={{ marginLeft: 12 }}>Retry</button>
            )}
          </div>
        )}
        {/* Post-adoption save reminder: modal over a dimmed backdrop,
            dismissed via the X close button, backdrop click, or "Got it". */}
        {saveReminder && !saveNoticeDismissed && (
          <DownloadSaveNoticeModal
            open
            title={saveNoticeTitle}
            confirmText={saveNoticeConfirmText}
            message={saveReminder}
            onClose={() => {
              setSaveNoticeDismissed(true);
              setSaveReminder(null);
              const scrollToTop = () => {
                const panel = document.querySelector(".section_generator_panel_content");
                if (panel) panel.scrollTop = 0;
                const home = document.querySelector(".home_content");
                if (home) home.scrollTop = 0;
                const list = document.querySelector(".section_generator_panel_list");
                if (list) list.scrollTop = 0;
              };
              scrollToTop();
              if (typeof requestAnimationFrame !== "undefined") {
                requestAnimationFrame(scrollToTop);
              }
              setTimeout(scrollToTop, 100);
              setTimeout(scrollToTop, 300);
            }}
          />
        )}
        {flushing && <div className="screen-flush"></div>}
        {showError && (
          <ErrorOverlay
            isVisible={false}
            type={`${showError.type}`}
            title={`${showError.title}`}
            message={`${showError.message}`}
            details={{
              message: `${showError.details.message}`,
              code: `${showError.details.code}`,
              timestamp: new Date(),
              context: showError.details.context || {},
              stack: `${showError.details.stack}`,
            }}
            onDismiss={() => setShowError(defaultError)}
            showRetry={false}
            showReport={false}
            location={location}
          />
        )}
          {homePage === "auth" && (
          <AuthPage onSignIn={handleSignIn} version={selectedEnvironment?.id} notice={signInNotice} />
        )}
          {homePage === "organizationPicker" && (
          <AuthPage
            onSignIn={handleOrganizationChanged}
            version={selectedEnvironment?.id}
            organizationSelectionOnly
          />
        )}
        {showSettings && (
          <SettingsPage
            searchType={searchType}
            setSearchType={handleSearchTypeChange}
            getStoredValue={getStoredValue}
            handleClearProperties={handleClearProperties}
            setShowError={setShowError}
            titleMap={titleMap}
            changeEnvironment={changeEnvironment}
            logAuditAction={logAuditAction}
            reportTemplateProjectId={reportTemplateProjectId}
            isSignedIn={signedIn}
            onSignOut={handleSignOut}
          />
        )}
          {homePage === "main" && (
          <MainPage
            downloadContextUnavailable={downloadContextUnavailable}
            trackChanges={trackChanges}
            handleTrackChangesToggle={handleTrackChangesToggle}
            // insertTextAtSection={insertTextAtSection}
            insertAtSection={insertAtSection}
            hasSectionBookmarks={hasSectionBookmarks}
            scrollToSection={scrollToSection}
            scrollToGroundingReference={scrollToGroundingReference}
            retrieveLLMOuputAsync={retrieveLLMOuputAsync}
            // createTable={createTable}
            getSelectedText={getSelectedText}
            selectedSections={selectedSections}
            setSelectedSections={setSelectedSections}
            generatedSections={generatedSections}
            setGeneratedSections={setGeneratedSections}
            sectionStatus={sectionStatus}
            sections={settingsGroup}
            templateTagsMap={templateTagsMap}
            selectedProject={projects.find((p) => p.id === selectedProjectId)}
            selectedReportTemplate={reportTemplates.find((template) => template.id === selectedReportTemplateId)}
            selectedReportTemplateId={selectedReportTemplateId}
            sectionEvalStatus={sectionEvalStatus}
            setSectionEvalStatus={setSectionEvalStatus}
            runTask={runTask}
            selectAllSections={selectAllSections}
            setSelectAllSections={setSelectAllSections}
            acceptChanges={acceptChanges}
            declineChanges={declineChanges}
            outputDecision={outputDecision}
            setOutputDecision={setOutputDecision}
            sectionDialogContent={sectionDialogContent}
            setSectionDialogContent={setSectionDialogContent}
            sourceDialogOpen={sourceDialogOpen}
            setSourceDialogOpen={setSourceDialogOpen}
            sourceDialogContent={sourceDialogContent}
            setSourceDialogContent={setSourceDialogContent}
            feedbackDialogOpen={feedbackDialogOpen}
            setFeedbackDialogOpen={setFeedbackDialogOpen}
            feedbackDialogContent={feedbackDialogContent}
            setFeedbackDialogContent={setFeedbackDialogContent}
            regenerateDialogOpen={regenerateDialogOpen}
            setRegenerateDialogOpen={setRegenerateDialogOpen}
            regenerateDialogContent={regenerateDialogContent}
            setRegenerateDialogContent={setRegenerateDialogContent}
            toggleRegenerateOptions={toggleRegenerateOptions}
            regenerateLoading={regenerateLoading}
            setRegenerateLoading={setRegenerateLoading}
            qualityCheckDialogOpen={qualityCheckDialogOpen}
            setQualityCheckDialogOpen={setQualityCheckDialogOpen}
            qualityCheckDialogContent={qualityCheckDialogContent}
            setQualityCheckDialogContent={setQualityCheckDialogContent}
            //formatDateToLongForm={formatDateToLongForm}
            bookmarks={bookmarks}
            projectDetailsExpanded={projectDetailsExpanded}
            setProjectDetailsExpanded={setProjectDetailsExpanded}
            projectPlaceholderDetails={projectPlaceholderDetails}
            messages={messages}
            setMessages={setMessages}
            messageChatId={messageChatId}
            setMessageChatId={setMessageChatId}
            onChatContextReset={() => setMessageChatId(null)}
            selectedText={selectedText}
            retrievellmOuputWOGroup={retrievellmOuputWOGroup}
            retrievellmOuputWOGroupAsync={retrievellmOuputWOGroupAsync}
            retrieveSourceDocument={retrieveSourceDocument}
            uploadedFiles={uploadedFiles}
            retreiveSourceDocumentMetadata={retreiveSourceDocumentMetadata}
            uploadDocument={uploadDocument}
            deleteDocument={deleteDocument}
            deleteTag={deleteTag}
            setShowError={setShowError}
            titleMap={titleMap}
            setTest={setTest}
            location={location}
            uploadedAgentFiles={uploadedAgentFiles}
            setUploadedAgentFiles={setUploadedAgentFiles}
            transcribeAudio={transcribeAudio}
            synthesizeSpeech={synthesizeSpeech}
            retrieveAgentQuery={retrieveAgentQuery}
            streamGetRequest={streamGetRequest}
            retrieveAgentStatus={retrieveAgentStatus}
            retrieveAgentGrounding={retrieveAgentGrounding}
            retrieveChatHistory={retrieveChatHistory}
            retrieveChatAsyncTask={retrieveChatAsyncTask}
            waitForChatAsyncResult={waitForChatAsyncResult}
            sectionGeneration={sectionGeneration}
            setSectionGeneration={setSectionGeneration}
            fetchSettingsGroup={fetchSettingsGroup}
            reportTemplateId={selectedReportTemplateId}
            processDocument={processDocument}
            retrieveDocumentsList={retrieveDocumentsList}
            retrieveDocumentsForImageInsertion={retrieveDocumentsForImageInsertion}
            retrieveDocumentImages={retrieveDocumentImages}
            downloadDocumentImage={downloadDocumentImage}
            submitLangfuseFeedback={submitLangfuseFeedback}
            setSectionStatus={setSectionStatus}
            logAuditAction={logAuditAction}
            reportTemplateProjectId={reportTemplateProjectId}
            getProjectChatHistory={getProjectChatHistory}
            getChatThread={getChatThread}
            setChatPinned={setChatPinned}
            renameChatHistory={renameChatHistory}
            deleteChatHistory={deleteChatHistory}
            startAgentQuery={startAgentQuery}
            getAgentTaskStatus={getAgentTaskStatus}
            streamAgentTaskStatus={streamAgentTaskStatus}
            getModels={getModels}
            getProjectTags={getProjectTags}
            getPromptTemplates={getPromptTemplates}
          />
        )}
        {showMainDataPlaceholder &&
          (mainDataError ? (
            <div className="signin_org_picker">
              <div className="signin_error_alert" role="alert">
                {mainDataError}
              </div>
              <button type="button" className="signin_continue_button" onClick={handleSignIn}>
                Retry
              </button>
            </div>
          ) : (
            <div className="signin_org_loading" aria-live="polite">
              <Spin size="small" />
              <span>Loading your report...</span>
            </div>
          ))}
          {homePage === "select" && (
          <SelectPage
            projects={projects}
            reportTemplates={reportTemplates}
            selectedProjectId={selectedProjectId}
            setSelectedProjectId={setSelectedProjectId}
            selectedReportTemplateId={selectedReportTemplateId}
            setSelectedReportTemplateId={setSelectedReportTemplateId}
            selectsDisabled={selectsDisabled}
            handleTemplateGeneration={handleTemplateGeneration}
          />
        )}
        <DocumentReadinessModal
          notice={templateDocumentReadinessNotice}
          onClose={clearTemplateDocumentReadinessNotice}
          projectId={selectedProjectId}
          projectName={projects.find((project) => project.id === selectedProjectId)?.name}
        />
      </div>
      <Footer>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            justifyContent: "flex-start",
            width: "auto",
          }}
        >
          <div className="header_icon" onClick={handleSettingsToggle}>
            <Tooltip autoAdjustOverflow title="Settings" placement="right">
              <SettingOutlined />
            </Tooltip>
          </div>
          <span className="header_title">{`ENV: ${
            selectedEnvironment?.id === "prod"
              ? "Production"
              : selectedEnvironment?.id === "test"
              ? "Test"
              : selectedEnvironment?.id === "staging"
              ? "Staging"
              : selectedEnvironment?.id === "pilot"
              ? "Pilot"
              : selectedEnvironment?.id === "preprod"
              ? "Preprod"
              : "Unknown"
          }`}</span>
        </div>
        <div
          className="header_title"
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
          }}
        >
          {sectionGeneration ? (
            <span className="processing-container">
              <LoadingOutlined spin style={{ fontSize: "1rem" }} />
              <span className="processing-text">Working...</span>
            </span>
          ) : showDone ? (
            <span className="done-text">
              <CheckCircleOutlined style={{ fontSize: "1rem" }} />
              Done
            </span>
          ) : null}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            justifyContent: "flex-end",
            width: "auto",
            paddingRight: "10px",
            boxSizing: "border-box",
          }}
        >
          <span className={`header_title ${!isOnline ? "offline-indicator-text" : "online-indicator-text"}`}>
            {isOnline ? (
              <div>
                <CloudRegular fontSize={22} /> <div>Online</div>
              </div>
            ) : (
              <div>
                <CloudOffRegular fontSize={22} /> <div>Offline</div>
              </div>
            )}
          </span>
          {(selectedEnvironment?.id === "prod" ||
            selectedEnvironment?.id === "pilot" ||
            selectedEnvironment?.id === "preprod") && <div style={{ minWidth: "0px" }}></div>}
          {showSettings && (selectedEnvironment?.id === "staging" || selectedEnvironment?.id === "test") && (
            <div style={{ minWidth: "0px" }}></div>
          )}
          {/* {test} */}
          {signedIn && !showSettings && displayMainFunc && selectedEnvironment?.id !== "prod" && (
            <div style={{ width: "0px" }}></div>
            // <div
            //   className="header_icon"
            //   onClick={() => {
            //     setQualityCheckDialogOpen(true);
            //   }}
            // >
            //   <Tooltip autoAdjustOverflow title="Open" placement="left">
            //     <PlusCircleOutlined />
            //   </Tooltip>
            // </div>
          )}
        </div>
      </Footer>
    </div>
  );
}
